import { useState, useEffect, useRef, useMemo } from 'react';
import { observe } from '../utils/observe';

const useInView = ({
  threshold,
  delay,
  trackVisibility,
  rootMargin,
  root,
  triggerOnce,
  skip,
  initialInView,
  fallbackInView,
  onChange,
}) => {
  const [ref, setRef] = useState(null);
  const [state, setState] = useState({
    inView: !!initialInView,
    entry: undefined,
  });

  const callback = useRef();

  callback.current = onChange;

  const options = {
    root,
    rootMargin,
    threshold,
    trackVisibility,
    delay,
  };

  const memoThreshold = useMemo(
    () => (Array.isArray(threshold) ? threshold.toString() : threshold),
    [threshold]
  );

  useEffect(() => {
    if (skip || !ref) return;

    let unobserve = observe(
      ref,
      (inView, entry) => {
        setState({ inView, entry });

        if (callback.current) {
          callback.current(inView, entry);
        }

        if (entry.isIntersecting && triggerOnce && unobserve) {
          unobserve();
          unobserve = undefined;
        }
      },
      options,
      fallbackInView
    );

    return () => {
      if (unobserve) {
        unobserve();
      }
    };
  }, [
    memoThreshold,
    ref,
    root,
    rootMargin,
    triggerOnce,
    skip,
    trackVisibility,
    fallbackInView,
    delay,
  ]);

  const entryTarget = state.entry?.target;

  useEffect(() => {
    if (!ref && entryTarget && !triggerOnce && !skip) {
      setState({
        inView: !!initialInView,
        entry: undefined,
      });
    }
  }, [ref, entryTarget, triggerOnce, skip, initialInView]);

  const result = {
    ref: setRef,
    inView: state.inView,
    entry: state.entry,
  };

  return result;
};

export default useInView;
